import { useCallback } from 'react';
import {
    FieldValues,
    FormState,
    Path,
    PathValue,
    useFormContext,
    UseFormGetValues,
    UseFormSetValue,
} from 'react-hook-form';

import { parseTimeInput } from '../utils';

type TTimeInputForm<T extends FieldValues> = {
    getValues: UseFormGetValues<T>;
    setValue: UseFormSetValue<T>;
    formState: Pick<FormState<T>, 'isSubmitted'>;
};

/**
 * Builds an `onBlur` handler that rewrites a wall-clock field into its canonical `HH:mm` form,
 * so typing "6" leaves "06:00" behind. Input the parser does not understand is left exactly as
 * typed, for the field's own validation to report.
 *
 * Normalizing only re-runs validation once the form has been submitted, so a time field stays as
 * quiet as every other field until the user has tried to save.
 *
 * Reads the surrounding form context by default; a form hosting its own `FormProvider` has to
 * pass its methods in, since the context is not visible to the component that provides it.
 */
export const useTimeInputNormalizer = <T extends FieldValues = FieldValues>(form?: TTimeInputForm<T>) => {
    const context = useFormContext<T>();
    const { getValues, setValue, formState } = form ?? context;
    const { isSubmitted } = formState;

    return useCallback(
        (name: string) => () => {
            const path = name as Path<T>;
            const raw = getValues(path) as string | null | undefined;
            const parsed = parseTimeInput(raw);
            if (parsed === null || parsed === raw) return;

            setValue(path, parsed as PathValue<T, Path<T>>, { shouldValidate: isSubmitted, shouldDirty: true });
        },
        [getValues, setValue, isSubmitted],
    );
};
